import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { sectionSlugs, normalizeLang } from '@/mocks/sections';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [showMobileCats, setShowMobileCats] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false);
    setIsNavOpen(false);
  };

  const lang = normalizeLang(i18n.language);

  const navLinks = [
    { label: t('nav.home'), href: '/', icon: 'ri-home-4-line' },
    { label: t('nav.categories'), href: `/artigo/${sectionSlugs.categories[lang]}`, icon: 'ri-grid-line' },
    { label: t('nav.guides'), href: `/artigo/${sectionSlugs.guides[lang]}`, icon: 'ri-book-open-line' },
    { label: t('nav.qc'), href: `/artigo/${sectionSlugs.qc[lang]}`, icon: 'ri-shield-check-line' },
    { label: t('nav.shipping'), href: `/artigo/${sectionSlugs.shipping[lang]}`, icon: 'ri-truck-line' },
    { label: t('nav.comparisons'), href: `/artigo/${sectionSlugs.comparisons[lang]}`, icon: 'ri-scales-3-line' },
    { label: t('nav.safety'), href: `/artigo/${sectionSlugs.safety[lang]}`, icon: 'ri-shield-star-line' },
  ];

  const currentLang = lang;

  return (
    <nav className="sticky top-0 z-50 w-full bg-white border-b-2 border-background-200">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              CSSBuy<span className="text-primary-500">BR</span>.INK
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors rounded-md whitespace-nowrap"
              >
                <i className={`${link.icon} w-4 h-4 flex items-center justify-center`}></i>
                <span>{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-2.5">
            {/* Shopping Guide Button */}
            <Link
              to={`/artigo/${sectionSlugs.guides[lang]}`}
              className="hidden xl:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
              <span>{t('nav.shoppingGuide')}</span>
            </Link>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors rounded-md"
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center"></i>
                <span className="uppercase">{currentLang}</span>
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg border border-background-200/80 py-1 z-50">
                  <button
                    onClick={() => changeLanguage('pt')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${currentLang === 'pt' ? 'text-primary-500 font-semibold' : 'text-foreground-800'}`}
                  >
                    {t('lang.pt')}
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${currentLang === 'en' ? 'text-primary-500 font-semibold' : 'text-foreground-800'}`}
                  >
                    {t('lang.en')}
                  </button>
                  <button
                    onClick={() => changeLanguage('de')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${currentLang === 'de' ? 'text-primary-500 font-semibold' : 'text-foreground-800'}`}
                  >
                    {t('lang.de')}
                  </button>
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-bold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav.cta')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsNavOpen(!isNavOpen)}
            className="xl:hidden p-2 text-foreground-900 hover:text-primary-500 transition-colors"
            aria-label="Menu"
          >
            <i className={`${isNavOpen ? 'ri-close-line' : 'ri-menu-line'} w-6 h-6 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isNavOpen && (
        <div className="xl:hidden border-t border-background-200 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {/* Quick actions */}
            <div className="flex items-center gap-2 mb-3">
              <Link
                to="/"
                onClick={() => setIsNavOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-foreground-900 bg-background-100 rounded-lg"
              >
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                {t('nav.home')}
              </Link>
              <Link
                to={`/artigo/${sectionSlugs.guides[lang]}`}
                onClick={() => setIsNavOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-primary-600 bg-primary-50 rounded-lg"
              >
                <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
                {t('nav.shoppingGuide')}
              </Link>
            </div>

            {/* Nav links */}
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsNavOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-base font-medium text-foreground-900 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors"
              >
                <i className={`${link.icon} w-5 h-5 flex items-center justify-center text-primary-500`}></i>
                <span>{link.label}</span>
              </Link>
            ))}

            {/* Categories collapsible */}
            <div className="border-t border-background-200 pt-2 mt-2">
              <button
                onClick={() => setShowMobileCats(!showMobileCats)}
                className="w-full flex items-center justify-between px-3 py-3 text-base font-semibold text-foreground-900 rounded-lg"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-apps-2-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                  {t('dropdown.title')}
                </span>
                <i className={`${showMobileCats ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-5 h-5 flex items-center justify-center text-foreground-500`}></i>
              </button>
              {showMobileCats && (
                <div className="grid grid-cols-2 gap-1.5 px-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-background-100 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className="text-xs font-medium text-foreground-800 truncate">{t(cat.labelKey)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Language + CTA */}
            <div className="border-t border-background-200 pt-3 mt-2 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                {['pt', 'en', 'de'].map((lng) => (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`flex-1 px-3 py-2 text-sm font-semibold rounded-md transition-colors ${currentLang === lng ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-800 hover:bg-background-200'}`}
                  >
                    {lng.toUpperCase()}
                  </button>
                ))}
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-bold text-white bg-primary-500 rounded-lg"
              >
                <span>{t('nav.cta')}</span>
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}